import React, { createContext, useReducer } from "react";
import {
  IdentificationTypeIdEnum,
  SignupCniRequestAttributes,
  SignupPassportRequestAttributes,
  SignupRequestAttributes,
} from "../attributes/user-attributes";

const TEMP_KEY_DATA = "temp";

export interface SignupState {
  personal: SignupPersonalInfoState;
  id_info: SignupCniRequestAttributes | SignupPassportRequestAttributes;
}

export interface SignupPersonalInfoState extends SignupRequestAttributes {
  dial_code: string;
  secondary_dial_code: string;
  secondary_country_iso_code: string;
}

export enum SignupTypeEnum {
  loading,
  store,
  temp,
  clear,
}

interface SignupAction {
  type: SignupTypeEnum;
  payload?: SignupState;
}

export const SignupContext = createContext<SignupState>(initSignupState());

export const SignupDispatchContext = createContext<Function>(() => {});

export const SignupContextProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(SignupReducer, initSignupState());

  return (
    <SignupContext.Provider value={state}>
      <SignupDispatchContext.Provider value={dispatch}>
        {children}
      </SignupDispatchContext.Provider>
    </SignupContext.Provider>
  );
};

// Redux

function SignupReducer(state: SignupState, action: SignupAction): SignupState {
  switch (action.type) {
    case SignupTypeEnum.store:
      return {
        ...state,
        personal: { ...state.personal, ...getTempSessionData() },
        ...action.payload,
      };
    case SignupTypeEnum.temp:
      setTempSessionData(action.payload.personal);
      return {
        ...state,
        personal: { ...state.personal, ...action.payload.personal },
      };
    case SignupTypeEnum.clear:
      clearTempSessionData();
      return { ...state, ...initSignupState() };
    default:
      return state;
  }
}

function initSignupState(): SignupState {
  const temp = getTempSessionData();

  const state = {
    personal: {
      country_iso_code: "",
      dial_code: "",
      email: "",
      telephone: "",
      secondary_telephone: "",
      secondary_dial_code: "",
      secondary_country_iso_code: "",
      pin_code: "",
      last_name: "",
      first_name: "",
      profile_picture: null,
      data_of_birth: "",
      birth_place: "",
      address: "",
      city: "",
    },
    id_info: {
      id_number: "",
      identification_type_id: IdentificationTypeIdEnum.cni,
      cni_back: null,
      cni_front: null,
      expiration_date: "",
      issue_date: "",
      passport: null,
    },
  };

  if (temp !== null) {
    return {
      ...state,
      personal: { ...state.personal, ...temp },
    };
  }

  return state;
}

export function stepOneFinished(): boolean {
  const temp = getTempSessionData();

  return (
    temp !== null &&
    temp.dial_code.length !== 0 &&
    temp.country_iso_code.length !== 0 &&
    temp.telephone.length !== 0 &&
    temp.pin_code.length !== 0
  );
}

function setTempSessionData(data: any): void {
  sessionStorage.setItem(TEMP_KEY_DATA, JSON.stringify(data));
}

function clearTempSessionData(): void {
  sessionStorage.clear();
}

function getTempSessionData(): any {
  let json = sessionStorage.getItem(TEMP_KEY_DATA);

  if (json !== null && json !== undefined) {
    return JSON.parse(json);
  }

  return null;
}
